import React, { useState } from "react"
import moment from "moment"
import firebase from "firebase"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { db } from "../../utils/firebase"
import "./sabine.scss"
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import {
	DEFAULT_TEST_ACCOUNT_IDS,
	buildCanonicalConversionDataset,
	buildNewRepeatBreakdown,
	getBookingReferenceSeconds,
} from "../../utils/analytics/conversion-metrics"
import {
	toMillis,
	chunkArray,
	calculateRate,
	formatPercentFromValue,
	formatDecimal,
	formatPaymentsWithAverage,
	formatCompactDuration,
} from "../../utils/analytics/sabine-metrics"

const CLIENT_QUERY_CHUNK_SIZE = 10
const TEST_ACCOUNT_SET = new Set(DEFAULT_TEST_ACCOUNT_IDS)
const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000

let yearsObj = {}
const nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

const calculateTotals = (rows) => {
	if (!rows.length) return null

	const totals = rows.reduce(
		(acc, row) => {
			acc.bookingsMade += row.bookingsMade
			acc.marginPercentTotal += row.marginPercentTotal
			acc.marginPercentCount += row.marginPercentCount
			acc.newEnquiries += row.newEnquiries
			acc.newBookings += row.newBookings
			acc.repeatEnquiries += row.repeatEnquiries
			acc.repeatBookings += row.repeatBookings
			acc.combinedEnquiries += row.combinedEnquiries
			acc.combinedBookings += row.combinedBookings
			acc.quoteCountTotal += row.quoteCountTotal
			acc.timeLagTotalSeconds += row.timeLagTotalSeconds
			acc.timeLagCount += row.timeLagCount
			acc.paymentsTotal += row.paymentsTotal
			return acc
		},
		{
			bookingsMade: 0,
			marginPercentTotal: 0,
			marginPercentCount: 0,
			newEnquiries: 0,
			newBookings: 0,
			repeatEnquiries: 0,
			repeatBookings: 0,
			combinedEnquiries: 0,
			combinedBookings: 0,
			quoteCountTotal: 0,
			timeLagTotalSeconds: 0,
			timeLagCount: 0,
			paymentsTotal: 0,
		}
	)

	return {
		label: "Overall",
		bookingsMade: totals.bookingsMade,
		avgMarginDisplay: totals.marginPercentCount > 0 ? `${(totals.marginPercentTotal / totals.marginPercentCount).toFixed(2)}%` : "N/A",
		newEnquiries: totals.newEnquiries,
		newConversionDisplay: formatPercentFromValue(calculateRate(totals.newBookings, totals.newEnquiries)),
		repeatEnquiries: totals.repeatEnquiries,
		repeatConversionDisplay: formatPercentFromValue(calculateRate(totals.repeatBookings, totals.repeatEnquiries)),
		combinedConversionDisplay: formatPercentFromValue(calculateRate(totals.combinedBookings, totals.combinedEnquiries)),
		quotesPerEnquiryDisplay: formatDecimal(totals.combinedEnquiries > 0 ? totals.quoteCountTotal / totals.combinedEnquiries : 0),
		timeToQuoteDisplay: totals.timeLagCount > 0 ? formatCompactDuration(totals.timeLagTotalSeconds / totals.timeLagCount) : "N/A",
		paymentsDisplay: formatPaymentsWithAverage(totals.paymentsTotal, totals.bookingsMade),
	}
}

export default function Sabine() {
	const [loading, setLoading] = useState(false)
	const [hasFetched, setHasFetched] = useState(false)
	const [month, setMonth] = useState(moment().format("MMMM"))
	const [year, setYear] = useState(moment().format("YYYY"))
	const [rows, setRows] = useState([])
	const [totalsRow, setTotalsRow] = useState(null)

	const buildClientHistoryMapForClients = async (clientIds) => {
		const uniqueClientIds = [...new Set((clientIds || []).filter(Boolean))]
		if (!uniqueClientIds.length) return new Map()

		const historyMap = new Map()
		const clientChunks = chunkArray(uniqueClientIds, CLIENT_QUERY_CHUNK_SIZE)
		const snapshots = await Promise.all(clientChunks.map((chunk) => db.collection("bookings").where("client", "in", chunk).get()))

		snapshots.forEach((snapshot) => {
			snapshot.docs.forEach((doc) => {
				const data = doc.data()
				const bookingClientId = data.client
				const bookingReferenceSeconds = getBookingReferenceSeconds(data)
				if (!bookingClientId || !bookingReferenceSeconds) return

				if (!historyMap.has(bookingClientId) || historyMap.get(bookingClientId) > bookingReferenceSeconds) {
					historyMap.set(bookingClientId, bookingReferenceSeconds)
				}
			})
		})

		return historyMap
	}

	const fetchQuotesByClients = async (clientIds) => {
		const uniqueClientIds = [...new Set((clientIds || []).filter(Boolean))]
		if (!uniqueClientIds.length) return new Map()

		const quotesByClient = new Map()
		const clientChunks = chunkArray(uniqueClientIds, CLIENT_QUERY_CHUNK_SIZE)
		const snapshots = await Promise.all(clientChunks.map((chunk) => db.collection("quotes").where("client", "in", chunk).get()))

		snapshots.forEach((snapshot) => {
			snapshot.docs.forEach((doc) => {
				const data = doc.data()
				const clientId = data.client
				const createdMillis = toMillis(data.created)

				if (!clientId || !createdMillis) return

				if (!quotesByClient.has(clientId)) {
					quotesByClient.set(clientId, [])
				}
				quotesByClient.get(clientId).push(createdMillis)
			})
		})

		quotesByClient.forEach((timestamps, clientId) => {
			quotesByClient.set(
				clientId,
				timestamps.sort((a, b) => a - b)
			)
		})

		return quotesByClient
	}

	const fetchInitialPaymentsByBooking = async (bookings) => {
		const paymentTotalsByBooking = new Map()

		await Promise.all(
			bookings.map(async (booking) => {
				const bookingCreatedMillis = booking.createdMillis
				if (!bookingCreatedMillis) {
					paymentTotalsByBooking.set(booking.id, 0)
					return
				}

				const cutoffMillis = bookingCreatedMillis + FORTY_EIGHT_HOURS_MS
				const paymentsSnapshot = await db.collection(`bookings/${booking.id}/payments`).get()

				let bookingPaymentTotal = 0

				paymentsSnapshot.docs.forEach((paymentDoc) => {
					const paymentData = paymentDoc.data()
					const createdMillis = toMillis(paymentData.created)
					const paidDateMillis = toMillis(paymentData.paid_date)
					const createdInRange = !!createdMillis && createdMillis >= bookingCreatedMillis && createdMillis <= cutoffMillis
					const paidDateInRange = !!paidDateMillis && paidDateMillis >= bookingCreatedMillis && paidDateMillis <= cutoffMillis

					if (!createdInRange && !paidDateInRange) return

					const amount = Number.parseFloat(paymentData.amount || 0)
					if (!Number.isNaN(amount)) {
						bookingPaymentTotal += amount
					}
				})

				paymentTotalsByBooking.set(booking.id, bookingPaymentTotal)
			})
		)

		return paymentTotalsByBooking
	}

	const fetchReport = async () => {
		if (!month || !year) return

		setLoading(true)
		setHasFetched(true)
		setRows([])
		setTotalsRow(null)

		try {
			const windowStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
			const windowEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()

			const startTimestamp = firebase.firestore.Timestamp.fromMillis(windowStart)
			const endTimestamp = firebase.firestore.Timestamp.fromMillis(windowEnd)

			const [agentDocs, openEnquiriesSnapshot, closedEnquiriesSnapshot, enquiryBookingsSnapshot, bookingsCreatedInRangeSnapshot] = await Promise.all([
				db.collection("users").where("show_for.targets", "==", true).orderBy("first_name", "asc").get(),
				db.collection("enquiries").where("created", ">=", startTimestamp).where("created", "<=", endTimestamp).get(),
				db.collection("enquiries_removed").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).get(),
				db.collection("bookings").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).get(),
				db.collection("bookings").where("created", ">=", startTimestamp).where("created", "<=", endTimestamp).get(),
			])

			const activeAgents = agentDocs.docs.map((doc) => {
				const data = doc.data()
				return {
					id: doc.id,
					name: `${data.first_name} ${data.last_name}`,
				}
			})
			const activeAgentIds = activeAgents.map((agent) => agent.id)
			const activeAgentSet = new Set(activeAgentIds)

			if (!activeAgents.length) {
				setRows([])
				setTotalsRow(null)
				return
			}

			const conversionDataset = buildCanonicalConversionDataset({
				openEnquiryDocs: openEnquiriesSnapshot.docs,
				removedEnquiryDocs: closedEnquiriesSnapshot.docs,
				enquiryBookingDocs: enquiryBookingsSnapshot.docs,
				directBookingDocs: bookingsCreatedInRangeSnapshot.docs,
			})

			const finalEnquiries = conversionDataset.finalEnquiries.filter((enquiry) => activeAgentSet.has(enquiry.agent))
			const conversionBookings = conversionDataset.allBookings.filter((booking) => activeAgentSet.has(booking.agent))

			const monthCreatedBookings = bookingsCreatedInRangeSnapshot.docs
				.map((doc) => ({
					id: doc.id,
					...doc.data(),
					createdMillis: toMillis(doc.data().created),
				}))
				.filter((booking) => {
					if (!activeAgentSet.has(booking.agent)) return false
					if (booking.removed === true) return false
					if (!booking.client) return false
					return !TEST_ACCOUNT_SET.has(booking.client)
				})

			const relevantClientIds = [
				...new Set(
					[...finalEnquiries.map((enquiry) => enquiry.client), ...conversionBookings.map((booking) => booking.client)].filter(
						(clientId) => clientId && !TEST_ACCOUNT_SET.has(clientId)
					)
				),
			]

			const [historyMap, quotesByClient, initialPaymentsByBooking] = await Promise.all([
				buildClientHistoryMapForClients(relevantClientIds),
				fetchQuotesByClients(relevantClientIds),
				fetchInitialPaymentsByBooking(monthCreatedBookings),
			])

			const reportRows = activeAgents.map((agent) => {
				const agentEnquiries = finalEnquiries.filter((enquiry) => enquiry.agent === agent.id)
				const agentConversionBookings = conversionBookings.filter((booking) => booking.agent === agent.id)
				const agentMonthBookings = monthCreatedBookings.filter((booking) => booking.agent === agent.id)

				const newRepeat = buildNewRepeatBreakdown({
					groupId: agent.id,
					groupKey: "agent",
					enquiries: finalEnquiries,
					bookings: conversionBookings,
					historyMap,
				})

				let marginPercentTotal = 0
				let marginPercentCount = 0
				let paymentsTotal = 0

				agentMonthBookings.forEach((booking) => {
					paymentsTotal += initialPaymentsByBooking.get(booking.id) || 0

					const profit = Number.parseFloat(booking.margins?.profit)
					const customerPrice = Number.parseFloat(booking.margins?.customer_price)
					if (!Number.isNaN(profit) && !Number.isNaN(customerPrice) && customerPrice > 0) {
						marginPercentTotal += (profit / customerPrice) * 100
						marginPercentCount++
					}
				})

				let quoteCountTotal = 0
				let timeLagTotalSeconds = 0
				let timeLagCount = 0

				agentEnquiries.forEach((enquiry) => {
					const quoteTimestamps = quotesByClient.get(enquiry.client) || []
					if (!quoteTimestamps.length) return

					const firstQuoteIndex = quoteTimestamps.findIndex((quoteTimestamp) => quoteTimestamp > enquiry.created)
					if (firstQuoteIndex === -1) return

					quoteCountTotal += quoteTimestamps.length - firstQuoteIndex
					timeLagTotalSeconds += (quoteTimestamps[firstQuoteIndex] - enquiry.created) / 1000
					timeLagCount++
				})

				const averageMargin = marginPercentCount > 0 ? marginPercentTotal / marginPercentCount : null
				const averageQuotesPerEnquiry = agentEnquiries.length > 0 ? quoteCountTotal / agentEnquiries.length : 0
				const averageLagSeconds = timeLagCount > 0 ? timeLagTotalSeconds / timeLagCount : null

				return {
					id: agent.id,
					label: agent.name,
					bookingsMade: agentMonthBookings.length,
					avgMarginDisplay: averageMargin != null ? `${averageMargin.toFixed(2)}%` : "N/A",
					newEnquiries: newRepeat.newEnquiriesCount,
					newBookings: newRepeat.newBookingsCount,
					newConversionDisplay: formatPercentFromValue(newRepeat.newConversion),
					repeatEnquiries: newRepeat.repeatEnquiriesCount,
					repeatBookings: newRepeat.repeatBookingsCount,
					repeatConversionDisplay: formatPercentFromValue(newRepeat.repeatConversion),
					combinedEnquiries: agentEnquiries.length,
					combinedBookings: agentConversionBookings.length,
					combinedConversionDisplay: formatPercentFromValue(calculateRate(agentConversionBookings.length, agentEnquiries.length)),
					quotesPerEnquiryDisplay: formatDecimal(averageQuotesPerEnquiry),
					quoteCountTotal,
					timeToQuoteDisplay: averageLagSeconds != null ? formatCompactDuration(averageLagSeconds) : "N/A",
					timeLagTotalSeconds,
					timeLagCount,
					paymentsDisplay: formatPaymentsWithAverage(paymentsTotal, agentMonthBookings.length),
					paymentsTotal,
					marginPercentTotal,
					marginPercentCount,
				}
			})

			setRows(reportRows)
			setTotalsRow(calculateTotals(reportRows))
		} catch (err) {
			console.error("Error fetching Sabine report data:", err)
			setRows([])
			setTotalsRow(null)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Sabine Report</h1>
			</Title>

			<div className="page-filters extra-stats sabine-filters">
				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					selected={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					selected={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<Button
					filters={true}
					disabled={!month || !year}
					label="Fetch report"
					loadingText="Fetching..."
					loading={loading}
					onClick={fetchReport}
				/>
			</div>

			{hasFetched && (
					<Table
						className="conversions-table sabine-table"
						headings={["Agent", "Bkgs", "Avg Mgn %", "New Enq", "New CR", "Rep Enq", "Rep CR", "Comb CR", "Q/Enq", "Time to 1st Quote", "Payments 48h (Avg)"]}
						noResults={rows.length === 0}
						noResultsMessage={loading ? "Fetching report data..." : "No active sales agents found for this month"}>
					{rows.map((row) => (
						<tr key={row.id}>
							<td>{row.label}</td>
							<td className="number-cell">{row.bookingsMade}</td>
							<td className="number-cell">{row.avgMarginDisplay}</td>
							<td className="number-cell">{row.newEnquiries}</td>
							<td className="number-cell">{row.newConversionDisplay}</td>
							<td className="number-cell">{row.repeatEnquiries}</td>
							<td className="number-cell">{row.repeatConversionDisplay}</td>
							<td className="number-cell">{row.combinedConversionDisplay}</td>
							<td className="number-cell">{row.quotesPerEnquiryDisplay}</td>
							<td className="number-cell">{row.timeToQuoteDisplay}</td>
							<td className="number-cell">{row.paymentsDisplay}</td>
						</tr>
					))}

					{rows.length > 0 && totalsRow && (
						<tr className="overalls-row">
							<td>{totalsRow.label}</td>
							<td className="number-cell">{totalsRow.bookingsMade}</td>
							<td className="number-cell">{totalsRow.avgMarginDisplay}</td>
							<td className="number-cell">{totalsRow.newEnquiries}</td>
							<td className="number-cell">{totalsRow.newConversionDisplay}</td>
							<td className="number-cell">{totalsRow.repeatEnquiries}</td>
							<td className="number-cell">{totalsRow.repeatConversionDisplay}</td>
							<td className="number-cell">{totalsRow.combinedConversionDisplay}</td>
							<td className="number-cell">{totalsRow.quotesPerEnquiryDisplay}</td>
							<td className="number-cell">{totalsRow.timeToQuoteDisplay}</td>
							<td className="number-cell">{totalsRow.paymentsDisplay}</td>
						</tr>
					)}
				</Table>
			)}
		</Tile>
	)
}
